"use client";

import clsx from "clsx";
import type { CdmEvent } from "@/lib/sse/useSSE";
import { CdmState } from "@/components/cdm/CdmState";
import { LtrToken } from "@/components/cdm/LtrToken";
import { isPublic } from "@/lib/profile";
import { PrivateOnlyMark, PRIVATE_EDGE_CLASSES } from "@/components/scene/PrivateOnlyMark";
import { useTranslations } from "@/lib/compat/i18n";

// Gates suppressed on the public profile — the compliance cascade beat
// (AML / sanctions / Travel Rule / hard stops) is hidden from public audiences.
const PUBLIC_HIDDEN_GATES = new Set([
  "AMLScreening",
  "SanctionsListReference",
  "TravelRule",
  "StandardsComplianceHardStop_H1",
]);

/**
 * Screen 3.3 — The Gate Cascade. The demo's signature 30 seconds.
 *
 *   Vertical stack of policy gates evaluating live, top to bottom. The
 *   TravelRule gate enters INDETERMINATE; Vega routes it to a Compliance
 *   Officer; ~4 s later the OVERRIDDEN chip resolves with a supersedesRef
 *   pointing at the original INDETERMINATE — which remains visible
 *   greyed-out below.
 *
 *   Per design principle 4: the INDETERMINATE → OVERRIDDEN moment is sacred.
 *   The audit trail must visibly differentiate "passed clean" from "passed
 *   via human review."
 */

const ORDERED_GATES = [
  { gate: "LegalPartyOnboarding",             short: "Counterparty onboarded"           },
  { gate: "AMLScreening",                     short: "AML screening"                    },
  { gate: "EffectiveCeiling",                 short: "Trade within credit ceiling"      },
  { gate: "SanctionsListReference",           short: "Sanctions screening · OFAC + UAE" },
  { gate: "IntentMandate",                    short: "Trade intent + buyer mandate"     },
  { gate: "SmartLogisticsAssessmentRequired", short: "SmartLogistics assessment"        },
  { gate: "TaxAssessmentValidity",            short: "Tax filing in good standing"      },
  { gate: "TravelRule",                       short: "Travel Rule"                      },
];

export function GateCascade({
  decisions,
  settlementRef = "LC-2026-00042",
  className,
}: {
  decisions: CdmEvent[];
  /** Settlement-instrument reference shown in the header chip. T1 → "PE-2026-00042"; T2 → "LC-2026-00187"; T3 → "LC-2026-00342". */
  settlementRef?: string;
  className?: string;
}) {
  // Build per-gate latest decision + audit trail (greyed-out predecessors).
  const byGate = new Map<string, CdmEvent[]>();
  for (const ev of decisions) {
    const gate = String(ev.payload.gate ?? "");
    if (!gate) continue;
    if (!byGate.has(gate)) byGate.set(gate, []);
    byGate.get(gate)!.push(ev);
  }

  const pub = isPublic();
  const visibleGates = pub
    ? ORDERED_GATES.filter((g) => !PUBLIC_HIDDEN_GATES.has(g.gate))
    : ORDERED_GATES;
  const t = useTranslations("gateCascade");

  return (
    <div className={clsx("grid h-full gap-4", pub ? "grid-cols-1" : "grid-cols-3", className)}>
      <div className={clsx("glass flex flex-col p-5", !pub && "col-span-2")}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("kicker")}</p>
            <h3 className="scene-h2 mt-1 text-orbitra-paper">{t("title")}</h3>
          </div>
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
            {t("settlementPrefix")} <LtrToken mono={false}>{settlementRef}</LtrToken>
          </span>
        </div>

        {decisions.length === 0 && (
          <div className="mt-3 inline-flex w-fit items-center gap-2 rounded-full border border-state-financed/30 bg-state-financed/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-state-financed">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-state-financed" />
            {t("armedWaiting")}
          </div>
        )}

        <ul className="mt-4 flex flex-col gap-1">
          {visibleGates.map((g) => (
            <GateRow
              key={g.gate}
              gate={g.gate}
              short={g.short}
              history={byGate.get(g.gate) ?? []}
              privateOnly={PUBLIC_HIDDEN_GATES.has(g.gate)}
            />
          ))}
        </ul>
      </div>

      {!pub && (
        <div className="flex flex-col gap-3">
          <PrivateOnlyMark>
            <div className="glass p-4 text-[11px]">
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("vegaComplianceLabel")}</p>
              <p className="mt-2 text-orbitra-paper italic">
                "{t("vegaRoutingQuote")}"
              </p>
              <p className="mt-2 text-[10px] text-orbitra-muted">
                {t("vegaAishaFooter")} <LtrToken className="text-state-verified">ORBITRA-CO-002</LtrToken>
              </p>
            </div>
          </PrivateOnlyMark>

          <PrivateOnlyMark>
            <div className="glass p-4 text-[11px]">
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-policy-overridden">{t("auditPrimacyLabel")}</p>
              <p className="mt-2 text-orbitra-paper">
                <span className="font-mono text-policy-indeterminate">{t("auditIndeterminate")}</span> {t("auditRowsNotDeleted")} <em>{t("auditNotDeletedEmphasis")}</em>.
                {" "}{t("auditRemainGreyedPrefix")} <span className="font-mono text-policy-overridden">{t("auditOverridden")}</span> {t("auditRemainGreyedSuffix")} <span className="font-mono">{t("auditSupersedesRef")}</span>.
              </p>
              <p className="mt-2 text-[10px] text-orbitra-muted">
                {t("auditVisualNote")}
              </p>
            </div>
          </PrivateOnlyMark>
        </div>
      )}
    </div>
  );
}

function GateRow({
  gate,
  short,
  history,
  privateOnly = false,
}: {
  gate: string;
  short: string;
  history: CdmEvent[];
  privateOnly?: boolean;
}) {
  const markPrivate = privateOnly && !isPublic();
  const t = useTranslations("gateCascade");
  // Latest decision is the most recent one; history shows the trail before it.
  const latest = history[history.length - 1];
  if (!latest) {
    return (
      <li className={clsx(
        "flex items-center gap-3 rounded-md border border-orbitra-muted/20 bg-orbitra-ink/30 px-3 py-2 text-[11px]",
        markPrivate && PRIVATE_EDGE_CLASSES,
      )}>
        {markPrivate && (
          <span aria-hidden="true" title={t("hiddenOnPublicTitle")} className="text-[10px] leading-none">🛑</span>
        )}
        <span className="h-2 w-2 rounded-full bg-orbitra-muted/40" />
        <span className="flex-1 text-orbitra-muted">{short}</span>
        <span className="font-mono text-[10px] text-orbitra-muted/70">{t("awaitingRow")}</span>
      </li>
    );
  }

  const outcome = String(latest.payload.outcome ?? "");
  const supersedesRef = latest.payload.supersedesRef as string | undefined;
  const rationale = latest.payload.rationale as string | undefined;
  const officerLei = latest.payload.officerLei as string | undefined;

  const tone =
    outcome === "PASS"          ? "border-policy-pass/40          bg-policy-pass/5          text-policy-pass" :
    outcome === "FAIL"          ? "border-policy-fail/40          bg-policy-fail/8          text-policy-fail" :
    outcome === "INDETERMINATE" ? "border-policy-indeterminate/45 bg-policy-indeterminate/8 text-policy-indeterminate shadow-[0_0_18px_var(--state-financed-glow)]" :
    outcome === "OVERRIDDEN"    ? "border-policy-overridden/45    bg-policy-overridden/8    text-policy-overridden shadow-[0_0_18px_rgba(124,58,184,0.25)]" :
                                  "border-orbitra-muted/30 text-orbitra-muted";

  return (
    <li className="flex flex-col gap-1">
      <div className={clsx("flex items-center gap-3 rounded-md border px-3 py-2 text-[11px]", tone, markPrivate && PRIVATE_EDGE_CLASSES)}>
        {markPrivate && (
          <span aria-hidden="true" title={t("hiddenOnPublicTitle")} className="text-[10px] leading-none">🛑</span>
        )}
        <span className={clsx(
          "h-2 w-2 rounded-full",
          outcome === "PASS"          && "bg-policy-pass shadow-[0_0_8px_var(--policy-pass)]",
          outcome === "FAIL"          && "bg-policy-fail shadow-[0_0_8px_var(--policy-fail)]",
          outcome === "INDETERMINATE" && "bg-policy-indeterminate shadow-[0_0_10px_var(--policy-indeterminate)] animate-pulse",
          outcome === "OVERRIDDEN"    && "bg-policy-overridden shadow-[0_0_10px_var(--policy-overridden)]",
        )} />
        <span className="flex-1 text-orbitra-paper">{short}</span>
        {rationale && (
          <span className="hidden truncate font-mono text-[9px] italic text-orbitra-muted xl:inline-block xl:max-w-[280px]">
            {rationale}
          </span>
        )}
        {officerLei && (
          <span className="font-mono text-[9px] text-policy-overridden">{t("officerPrefix")} {officerLei}</span>
        )}
        <CdmState token={outcome} className="px-1.5 py-0.5" />
      </div>

      {/* Audit trail: when the latest is OVERRIDDEN, show the original
          INDETERMINATE row underneath, greyed out, with a supersedes link. */}
      {outcome === "OVERRIDDEN" && supersedesRef && history.length >= 2 && (
        <div className="ms-6 flex items-center gap-2 rounded-md border border-policy-greyed-original/25 bg-orbitra-ink/30 px-3 py-1.5 text-[10px] text-policy-greyed-original">
          <span className="font-mono">{t("supersedesArrow")}</span>
          <LtrToken>{supersedesRef}</LtrToken>
          <span className="ms-2 italic">{t("supersedesTail")}</span>
          <CdmState token="INDETERMINATE" className="ms-auto px-1.5 py-0.5 opacity-60" />
        </div>
      )}
    </li>
  );
}
